/**
 * GFG-364 QA round 2026-10-14 (R4): buying the credits the funding wallet lacks, through JR's Buy
 * Credits modal (the Wallet's own) instead of a card charge inside the additions flow. What the
 * payment step shows is `creditsShortfallView`'s decision; this hook feeds it the caller's
 * permission, the wallet's rate, whether the wallet has a card and the purchase made, and wires the
 * modal back into the wizard: the purchase is recorded (its id rides on the confirm) and the quote
 * is asked again. "Check again" on a purchase still processing reads its status once first, so one
 * that settled or failed since stops showing as processing.
 */

import { type ComponentProps, type Dispatch, useEffect, useState } from "react";
import type BuyCreditsModal from "@/components/usersOrgs/BuyCreditsModal";
import { COPY } from "@/constants/campaignContacts";
import { useCheckCreditPurchase, useCreditSummary } from "@/hooks/useCredits";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { usePermissions } from "@/hooks/usePermissions";
import type { RecipientAdditionsQuote } from "@/interfaces/campaignContacts";
import { defaultFundingCard } from "@/utils/payment-helper";
import {
  creditsShortfallView,
  type PurchaseStatusOutcome,
  purchaseStatusOutcome,
} from "./creditsShortfall-helper";
import type {
  AdditionCreditsCopy,
  CreditsShortfallView,
  WizardAction,
  WizardPurchase,
  WizardState,
} from "./types";

type BuyCreditsModalProps = ComponentProps<typeof BuyCreditsModal>;

interface AdditionCreditsPurchaseInput {
  /** Absent until the first quote arrives. */
  quote: RecipientAdditionsQuote | undefined;
  state: Pick<WizardState, "purchase">;
  dispatch: Dispatch<Extract<WizardAction, { type: "purchased" | "purchaseResolved" }>>;
  /** The campaign's IANA zone, for the lot-expiry dates. */
  timeZone: string;
  /** Asks the server for the quote again: after a purchase, and on "Check again". */
  refetchQuote: () => void;
  /** Rewords the shortfall sentences (gift links); absent = the contacts copy. */
  creditsCopy?: AdditionCreditsCopy;
}

interface AdditionCreditsPurchase {
  view: CreditsShortfallView;
  openBuy: () => void;
  checkAgain: () => void;
  /** The wallet is re-read before the modal opens, or a purchase's status is: hold the controls. */
  busy: boolean;
  /** The modal's props; null when nobody here may buy (no permission, or the group may not). */
  buyModal: BuyCreditsModalProps | null;
}

const NO_SHORTFALL: CreditsShortfallView = { kind: "none" };

export function useAdditionCreditsPurchase(
  i: AdditionCreditsPurchaseInput,
): AdditionCreditsPurchase {
  const { quote, state, dispatch, refetchQuote } = i;
  const { can } = usePermissions();
  const canBuyCredit = can("buy", "CREDIT");
  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  // The Wallet's own check (`buy CREDIT` and a group enabled to purchase). Without it there is no
  // modal, so neither the summary (CREDIT view) nor the cards are asked for: a Campaign Manager
  // would only be refused.
  const groupId = canBuyCredit && quote?.walletCanBuy ? quote.walletGroupId : null;
  const groupName = quote?.walletName ?? null;
  const summaryQuery = useCreditSummary(groupId);
  const cardsQuery = usePaymentMethods(groupId);
  const purchaseCheck = useCheckCreditPurchase();
  const card = defaultFundingCard(cardsQuery.data ?? []);
  // Nothing says the wallet has no card until its cards are in; the modal refuses without one too.
  const hasCard = cardsQuery.data === undefined || card !== null;

  const view =
    quote === undefined
      ? NO_SHORTFALL
      : creditsShortfallView(
          {
            quote,
            canBuyCredit,
            purchase: state.purchase,
            timeZone: i.timeZone,
            creditsPerUsd: summaryQuery.data?.creditsPerUsd ?? null,
            hasCard,
          },
          i.creditsCopy,
        );
  const buy = view.kind === "buy" ? view : null;

  // The no-card message has no button to press: coming back to this tab (from the Wallet, where
  // the card is added) re-reads the cards, so BUY CREDITS returns once there is one.
  const noCard = view.kind === "noCard";
  const refetchCards = cardsQuery.refetch;
  useEffect(() => {
    if (!noCard) return;
    const refresh = () => void refetchCards();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [noCard, refetchCards]);

  /** Re-reads the cards and the rate first, so a card added or removed in another tab is seen. */
  async function openBuy() {
    if (groupId === null) return;
    setOpening(true);
    const [cards] = await Promise.all([cardsQuery.refetch(), summaryQuery.refetch()]);
    setOpening(false);
    // A loaded list with no card: the view now says so instead of offering the modal. Cards that
    // could not be read open it anyway (card null): the modal refuses with its own no-card message,
    // so the click always gives feedback.
    if (cards.data === undefined || defaultFundingCard(cards.data) !== null) setOpen(true);
  }

  /** The purchase's status, read once; a read that fails leaves it processing (ask again later). */
  function readOutcome(gid: number, purchaseId: number): Promise<PurchaseStatusOutcome> {
    return purchaseCheck.mutateAsync({ groupId: gid, purchaseId }).then(
      (purchase) => purchaseStatusOutcome(purchase.status),
      (): PurchaseStatusOutcome => "pending",
    );
  }

  async function checkAgain() {
    const pending = state.purchase?.outcome === "pending" ? state.purchase : null;
    if (pending !== null && groupId !== null) {
      const outcome = await readOutcome(groupId, pending.id);
      if (outcome !== "pending") dispatch({ type: "purchaseResolved", outcome });
    }
    refetchQuote();
  }

  function onPurchased(purchaseId: number, outcome: WizardPurchase["outcome"]) {
    dispatch({ type: "purchased", purchase: { id: purchaseId, outcome } });
    refetchQuote();
  }

  const buyModal: BuyCreditsModalProps | null =
    groupId === null || groupName === null
      ? null
      : {
          open,
          groupId,
          groupName,
          summary: summaryQuery.data,
          card,
          onClose: () => setOpen(false),
          initialUsd: buy?.initialUsd,
          minUsd: buy?.minUsd,
          onPurchased,
          noCardMessage: COPY.addWizard.credits.noCard,
        };

  return {
    view,
    openBuy: () => void openBuy(),
    checkAgain: () => void checkAgain(),
    busy: opening || purchaseCheck.isPending,
    buyModal,
  };
}
